/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useContext, useEffect, useState } from "react";
import Button, { Icons } from "../buttons/Button";
import DeleteModal, { DeleteModalIcons } from "../modal/DeleteModal";
import { errorAlert, makeApiRequest, successAlert } from "../../util";
import AuthContext from "../../store/authContext";
import InlineLoader from "../loader/InlineLoader";
import EditDFSIGroupRowFormFields from "./EditDFSIGroupRowFormFields";
import useHideComponentonOutsideClick from "../../hooks/useHideComponentonOutsideClick";
import {
  EDIT_DFSI_GROUP_TRANSLATIONS,
  EDIT_ROW_TRANSLATIONS,
  LOADING_TEXT_TRANSLATIONS,
} from "../../translations/util";
import { EDIT_DFSI_GROUP_FORM_FIELDS } from "../../constants/editFormFields";

interface IProps {
  setShowEditPanel: any;
  org: string;
  panelName: string;
  esRef: string;
  initialState: any;
  header: string;
  transformResponseRow: any;
  transformUpdateBody?: any;
  onUpdate?: any;
  loadDataByRef: any;
  deleteModalItemName?: any;
  onDelete?: any;
  hasDeletePermission?: boolean;
  hasEditPermission?: boolean;
  refreshTable?: any;
  formFields: any;
}

const isFormInvalid = ({ data }: { data: any }) => {
  if (!data) return true;

  const isConnectionFormInvalid = EDIT_DFSI_GROUP_FORM_FIELDS.map(
    (rows: any) => {
      const res = rows.fields.map((field: any) => {
        switch (field.type) {
          case "NUMBER":
            return field.isInputInvalid?.(
              data[field.name],
              data["ENCRYPTION-ENABLED"]
            );
          case "SearchableMultiSelect":
            return field.isInputInvalid?.(data[field.name]);
          default:
            return false;
        }
      });

      return res.reduce((acc: any, el: any) => acc || el);
    }
  ).reduce((acc: any, el: any) => acc || el);

  return isConnectionFormInvalid;
};

const EditDFSIGroupRow = ({
  setShowEditPanel,
  onDelete,
  esRef,
  org,
  panelName,
  initialState,
  header,
  transformResponseRow,
  transformUpdateBody,
  onUpdate,
  loadDataByRef,
  deleteModalItemName,
  hasDeletePermission = false,
  hasEditPermission = false,
  refreshTable,
  formFields,
}: IProps) => {
  const [open, setOpen] = useState(false);
  const [openEdit, setOpenEdit] = useState(false);
  const { token } = useContext(AuthContext);
  const [isEditing, setIsEditing] = useState(false);
  const [dataLoading, setDataLoading] = useState(false);
  const [data, setData] = useState(initialState);
  const [initialData, setInitialData] = useState(data);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState();
  const { setIsComponentVisible } = useHideComponentonOutsideClick(false);
  const [channelIds, setChannelIds] = useState<{ id: string; text: string }[]>(
    []
  );

  const getDataByRef = async (ref: string) => {
    if (org && token) {
      setDataLoading(true);
      try {
        const response = await loadDataByRef({
          token,
          org,
          ref,
        });
        setData(transformResponseRow(response));
        setInitialData(transformResponseRow(response));
        setSelected(
          response["CHANNEL-IDS"].map((row: any) => ({
            id: row["CHANNEL-ID"],
            text: row["CHANNEL-ID"],
          }))
        );
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
      } catch (e: any) {
        errorAlert(e);
      } finally {
        setDataLoading(false);
      }
    }
  };

  const updateData = async () => {
    setLoading(true);
    try {
      await onUpdate({
        body: transformUpdateBody(data),
        token,
        ref: esRef,
        org,
      });
      if (refreshTable) {
        refreshTable();
      }
      getDataByRef(esRef);
      successAlert(EDIT_DFSI_GROUP_TRANSLATIONS.successMsg);
      setIsEditing(false);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    } finally {
      setLoading(false);
    }
  };

  const getDFSIChannelIds = async (orgId: string) => {
    try {
      const response = await makeApiRequest<{
        Rows: { "CHANNEL-ID": string }[];
      }>({
        path: `/api/v1/app-config/dfsi-channels/ids/`,
        token,
        org: orgId,
      });
      setChannelIds(
        response.Rows.map((row) => ({
          id: row["CHANNEL-ID"],
          text: row["CHANNEL-ID"],
        }))
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      errorAlert(e);
    }
  };

  useEffect(() => {
    getDFSIChannelIds(org);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [org]);

  useEffect(() => {
    getDataByRef(esRef);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [esRef]);
  return (
    <>
      <aside
        id="sidepanel"
        data-cy="editDFSIGroup-sidepanel"
        className="sidepanel sidepanel-edit fixed z-40 h-full transform transition-transform ease-in duration-150 bg-white"
      >
        <div className="panel-header">
          <div className="panel-left-side">
            <Button
              kind="ghost"
              className="close-btn"
              icon={Icons.Close}
              onClick={() => setShowEditPanel()}
              dataCy="closeEditDFSIGroupPanel-"
            />
            <h5 data-cy="editDFSIGroup-panelName"> {panelName} </h5>
          </div>

          {!isEditing ? (
            <div className="panel-right-side tab-header-buttons">
              {onUpdate ? (
                <Button
                  className="w-sm mr-2"
                  kind="tertiary"
                  icon={Icons.Edit}
                  disabled={!hasEditPermission}
                  label={EDIT_DFSI_GROUP_TRANSLATIONS.btnLabel.edit}
                  onClick={() => {
                    setIsEditing(true);
                  }}
                  dataCy="editDFSIGroup-"
                />
              ) : null}

              {onDelete ? (
                <Button
                  kind="tertiary"
                  className="w-sm"
                  label={EDIT_DFSI_GROUP_TRANSLATIONS.btnLabel.delete}
                  disabled={!hasDeletePermission}
                  icon={Icons.Delete}
                  onClick={() => {
                    return setOpen(true);
                  }}
                  dataCy="deleteDFSIGroup-"
                />
              ) : null}
            </div>
          ) : (
            <div className="tab-header-buttons">
              <Button
                className="w-sm mr-2"
                type="button"
                kind="tertiary"
                disabled={false}
                label={EDIT_DFSI_GROUP_TRANSLATIONS.btnLabel.cancel}
                onClick={() => {
                  setOpenEdit(true);
                  setIsComponentVisible(false);
                }}
                dataCy="cancelEditDFSIGroup-"
              />
              <Button
                className="w-sm"
                type="button"
                loading={loading}
                disabled={isFormInvalid({ data })}
                onClick={updateData}
                label={EDIT_DFSI_GROUP_TRANSLATIONS.btnLabel.save}
                dataCy="saveEditDFSIGroup-"
              />
            </div>
          )}
        </div>

        <div className="panel-content">
          <div className="data-tabs">
            {dataLoading ? (
              <InlineLoader
                description={LOADING_TEXT_TRANSLATIONS.defaultLoadingText}
              />
            ) : (
              <EditDFSIGroupRowFormFields
                data={data}
                isEditing={isEditing}
                esRef={esRef}
                setData={setData}
                channelIds={channelIds}
                selectedItems={selected}
                setSelected={setSelected}
                formFields={formFields}
              />
            )}
          </div>
        </div>
      </aside>
      <DeleteModal
        modalHeaderText={EDIT_DFSI_GROUP_TRANSLATIONS.cancelModal.header(
          header
        )}
        modalDescriptionText={
          EDIT_DFSI_GROUP_TRANSLATIONS.cancelModal.description
        }
        onDelete={() => {
          setIsEditing(false);
          setData(initialData);
          setSelected(initialData["CHANNEL-IDS"]);
          setOpenEdit(false);
        }}
        open={openEdit}
        setOpen={setOpenEdit}
        confirmationButtonLabel={
          EDIT_ROW_TRANSLATIONS.CANCEL_MODAL.confirmationButtonLabel
        }
        cancelIcon={DeleteModalIcons.Cancel}
      />
      <DeleteModal
        modalHeaderText={EDIT_DFSI_GROUP_TRANSLATIONS.deleteModal.header(
          header
        )}
        modalDescriptionText={EDIT_DFSI_GROUP_TRANSLATIONS.deleteModal.description(
          header,
          data?.[deleteModalItemName]
        )}
        onDelete={() => onDelete(esRef)}
        open={open}
        setOpen={setOpen}
      />
    </>
  );
};

export default EditDFSIGroupRow;
